/**
 * TanStack Query cache configuration for POS system
 *
 * Stale times are tuned for:
 * - Operational data (tabs, cash): short - changes during service
 * - Reference data (products, categories, providers): long - changes infrequently
 * - Offline support: longer gcTime keeps data available when navigating
 */

const MS = 1000;
const MIN = 60 * MS;
const HOUR = 60 * MIN;

/** Default: 30 seconds - balances freshness vs refetch frequency */
export const DEFAULT_STALE_TIME = 30 * MS;

/** IMPORTANT ABOUT gcTime: Default: 8 hours -  if a waiter opens a menu category that hasn't been
 * viewed in 10 minutes (or any other time less than 8 hours), the data is still present
 * in memory and displays instantly, instead of triggering a loading spinner or an offline error screen
 * You get instant loading from memory (even after 8 hours), but it is instantly treated as old and
 * re-verified via a background API call
 */
export const DEFAULT_GC_TIME = 8 * HOUR;

/** Operational data - changes during service */
export const STALE_TIME = {
  /** Tabs change very frequently during operations */
  tabs: 15 * MS,
  /** Kitchen tickets change very frequently during service */
  kitchenTickets: 15 * MS,
  /** Reservations change as staff book during service */
  reservations: 15 * MS,
  /** Cash balances change with transactions */
  cashBalances: 30 * MS,
  shiftClosures: 30 * MS,
  cashWithdrawals: 30 * MS,
  /** Sales update as orders are completed */
  sales: 1 * MIN,
  invoiceStatus: 1 * MIN,
  messages: 1 * MIN,
  platformMessages: 1 * MIN,
} as const;

/** Product catalog - moderate change */
export const STALE_TIME_PRODUCTS = {
  products: 1 * MIN,
  productsForSales: 1 * MIN,
  productModifiers: 1 * MIN,
  productModifierCategories: 1 * MIN,
  ingredients: 1 * MIN,
  productIngredients: 1 * MIN,
  productCategories: 1 * MIN,
  productCategoriesForSales: 1 * MIN,
} as const;

/** Reference data - changes infrequently */
export const STALE_TIME_REFERENCE = {
  providers: 1 * MIN,
  paymentMethods: 1 * MIN,
  tablesLayout: 1 * MIN,
  organizationMembers: 1 * MIN,
  /** Tax regime changes very rarely; long stale time, cache keeps last value for offline POS. */
  companyRegime: 30 * MIN,
  /** CR territorial division from Hacienda; reference data, rarely changes. */
  locations: 30 * MIN,

  subscriptionCatalogPlans: 30 * MIN,
  subscriptionBillingHistory: 30 * MIN,
  subscriptionCurrentForOrg: 30 * MIN,
  organizationStatusPollInterval: 10 * MIN,

} as const;

/** External reference - updates daily dollar value */
export const STALE_TIME_DOLLAR = {
  dollarValue: 24 * HOUR,
} as const;

/** Organization & profile - changes rarely */
export const STALE_TIME_AUTH = {
  organization: 10 * MIN,
  profile: 10 * MIN,
  featureFlags: 24 * HOUR,
} as const;
